// PersonalRx — new flow graph editor backed by the explicit edge model
// (`personal_rx_flow_*` tables). Renders one node per question + one node
// per answer option, with real saved transitions.

import { useCallback, useEffect, useMemo, useState } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  Handle,
  Node as RFNode,
  Edge as RFEdge,
  Position,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import dagre from 'dagre';
import { Maximize2, Minimize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { loadDraftFlow } from '@/lib/personalRx/flowRepository';
import { validateFlow, type RxValidationReport } from '@/lib/personalRx/flowValidation';
import type {
  RxFlow,
  RxFlowNode,
  RxFlowOption,
  RxQuestionType,
  RxTransitionType,
} from '@/types/personalRxFlow';

const NODE_W = 260;
const NODE_H_QUESTION = 70;
const NODE_H_ANSWER = 34;

const TYPE_LABEL: Record<RxFlowNode['type'], string> = {
  start: 'התחלה',
  category_selection: 'בחירת תחום',
  question: 'שאלה',
  contact_form: 'טופס יצירת קשר',
  success: 'תודה',
};

const TRANSITION_LABEL: Record<RxTransitionType, string> = {
  question: 'מעבר לשאלה',
  continue: 'המשך לשאלה הבאה',
  end_category: 'סיום קטגוריה',
  contact_form: 'מעבר לטופס',
  success: 'מעבר למסך תודה',
};

type LayoutOut = { nodes: RFNode[]; edges: RFEdge[] };

function buildGraph(flow: RxFlow, selectedOptionId: string | null, categoryFilter: string): LayoutOut {
  const g = new dagre.graphlib.Graph();
  g.setGraph({ rankdir: 'LR', nodesep: 24, ranksep: 80 });
  g.setDefaultEdgeLabel(() => ({}));

  const rfNodes: RFNode[] = [];
  const rfEdges: RFEdge[] = [];

  const alwaysShow = (n: RxFlowNode) =>
    n.type === 'start' || n.type === 'category_selection' || n.type === 'contact_form' || n.type === 'success';
  const visibleNodes = categoryFilter === 'all'
    ? flow.nodes
    : flow.nodes.filter((n) => alwaysShow(n) || n.category === categoryFilter);
  const visibleIds = new Set(visibleNodes.map((n) => n.id));

  for (const n of visibleNodes) {
    const isQuestion = n.type === 'question' || n.type === 'category_selection';
    g.setNode(`q_${n.id}`, { width: NODE_W, height: isQuestion ? NODE_H_QUESTION : NODE_H_ANSWER });
    for (const o of n.options) {
      g.setNode(`a_${o.id}`, { width: NODE_W, height: NODE_H_ANSWER });
      g.setEdge(`q_${n.id}`, `a_${o.id}`);
      if (o.nextType === 'question' && o.nextNodeId && visibleIds.has(o.nextNodeId)) {
        g.setEdge(`a_${o.id}`, `q_${o.nextNodeId}`);
      }
      if (o.nextType === 'contact_form' && flow.contactFormNodeId && visibleIds.has(flow.contactFormNodeId)) {
        g.setEdge(`a_${o.id}`, `q_${flow.contactFormNodeId}`);
      }
      if (o.nextType === 'success' && flow.successNodeId && visibleIds.has(flow.successNodeId)) {
        g.setEdge(`a_${o.id}`, `q_${flow.successNodeId}`);
      }
    }
  }
  dagre.layout(g);

  for (const n of visibleNodes) {
    const p = g.node(`q_${n.id}`);
    rfNodes.push({
      id: `q_${n.id}`,
      position: { x: p.x - NODE_W / 2, y: p.y - NODE_H_QUESTION / 2 },
      data: { node: n },
      type: 'rxQuestion',
      sourcePosition: Position.Right,
      targetPosition: Position.Left,
      draggable: true,
    });
    for (const o of n.options) {
      const pa = g.node(`a_${o.id}`);
      rfNodes.push({
        id: `a_${o.id}`,
        position: { x: pa.x - NODE_W / 2, y: pa.y - NODE_H_ANSWER / 2 },
        data: { option: o, parent: n, selected: selectedOptionId === o.id },
        type: 'rxAnswer',
        sourcePosition: Position.Right,
        targetPosition: Position.Left,
        draggable: true,
      });
      rfEdges.push({
        id: `e_q_${n.id}_a_${o.id}`,
        source: `q_${n.id}`,
        target: `a_${o.id}`,
        style: { stroke: '#cbd5e1', strokeWidth: 1.5 },
      });
      const target =
        o.nextType === 'question' ? o.nextNodeId :
        o.nextType === 'contact_form' ? flow.contactFormNodeId :
        o.nextType === 'success' ? flow.successNodeId :
        null;
      if (target && !visibleIds.has(target)) continue;
      if (target) {
        rfEdges.push({
          id: `e_a_${o.id}_q_${target}`,
          source: `a_${o.id}`,
          target: `q_${target}`,
          animated: false,
          style: { stroke: '#10b981', strokeWidth: 2 },
          markerEnd: { type: MarkerType.ArrowClosed, color: '#10b981' },
        });
      }
    }
  }
  return { nodes: rfNodes, edges: rfEdges };
}

function QuestionNodeView({ data }: { data: { node: RxFlowNode } }) {
  const n = data.node;
  const color =
    n.type === 'start' ? 'bg-blue-50 border-blue-400' :
    n.type === 'success' ? 'bg-emerald-50 border-emerald-400' :
    n.type === 'contact_form' ? 'bg-amber-50 border-amber-400' :
    n.type === 'category_selection' ? 'bg-violet-50 border-violet-400' :
    'bg-white border-slate-300';
  return (
    <div className={`relative rounded-md border-2 px-3 py-2 shadow-sm ${color}`} style={{ width: NODE_W - 8 }}>
      <Handle type="target" position={Position.Left} style={{ background: '#64748b', width: 8, height: 8 }} />
      <div className="flex items-center justify-between gap-2">
        <Badge variant="secondary" className="text-[10px]">{TYPE_LABEL[n.type]}</Badge>
        {n.category && <span className="text-[10px] text-muted-foreground">{n.category}</span>}
      </div>
      <div className="mt-1 text-[12px] font-medium leading-tight line-clamp-2">{n.label || n.key}</div>
      <div className="text-[10px] text-muted-foreground mt-0.5 truncate">{n.key}</div>
      <Handle type="source" position={Position.Right} style={{ background: '#64748b', width: 8, height: 8 }} />
    </div>
  );
}

function AnswerNodeView({ data }: { data: { option: RxFlowOption; selected: boolean } }) {
  const o = data.option;
  const borderColor =
    o.nextType === 'question' ? 'border-emerald-400' :
    o.nextType === 'continue' ? 'border-slate-300' :
    o.nextType === 'end_category' ? 'border-orange-400' :
    o.nextType === 'contact_form' ? 'border-amber-400' :
    'border-rose-400';
  return (
    <div
      className={`relative rounded-full border-2 ${borderColor} bg-white px-3 py-1 text-[11px] shadow-sm flex items-center justify-between gap-2 ${
        data.selected ? 'ring-2 ring-blue-500' : ''
      }`}
      style={{ width: NODE_W - 8 }}
    >
      <Handle type="target" position={Position.Left} style={{ background: '#10b981', width: 8, height: 8 }} />
      <span className="truncate font-medium">{o.label}</span>
      <span className="text-[9px] text-muted-foreground whitespace-nowrap">{TRANSITION_LABEL[o.nextType]}</span>
      <Handle type="source" position={Position.Right} style={{ background: '#10b981', width: 8, height: 8 }} />
    </div>
  );
}

const nodeTypes = { rxQuestion: QuestionNodeView, rxAnswer: AnswerNodeView } as const;

export default function FlowGraphEditor() {
  const { toast } = useToast();
  const [flow, setFlow] = useState<RxFlow | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [editingNode, setEditingNode] = useState<RxFlowNode | null>(null);
  const [editingOption, setEditingOption] = useState<{ option: RxFlowOption; parent: RxFlowNode } | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    const f = await loadDraftFlow();
    setFlow(f);
    setLoading(false);
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const categories = useMemo(() => {
    if (!flow) return [];
    const s = new Set<string>();
    for (const n of flow.nodes) if (n.category) s.add(n.category);
    return Array.from(s).sort();
  }, [flow]);

  const { nodes, edges } = useMemo(
    () => flow ? buildGraph(flow, selectedOptionId, categoryFilter) : { nodes: [], edges: [] },
    [flow, selectedOptionId, categoryFilter],
  );

  const report: RxValidationReport | null = useMemo(
    () => flow ? validateFlow(flow) : null,
    [flow],
  );

  const selectedOption = useMemo(() => {
    if (!flow || !selectedOptionId) return null;
    for (const n of flow.nodes) {
      const o = n.options.find((x) => x.id === selectedOptionId);
      if (o) return { option: o, parent: n };
    }
    return null;
  }, [flow, selectedOptionId]);

  const updateOption = useCallback(
    (patch: Partial<RxFlowOption>) => {
      if (!flow || !selectedOption) return;
      setFlow({
        ...flow,
        nodes: flow.nodes.map((n) =>
          n.id !== selectedOption.parent.id ? n :
            { ...n, options: n.options.map((o) => o.id === selectedOption.option.id ? { ...o, ...patch } : o) },
        ),
      });
    },
    [flow, selectedOption],
  );

  const saveOptionPatch = useCallback(async () => {
    if (!selectedOption) return;
    setSaving(true);
    const { error } = await supabase
      .from('personal_rx_flow_options')
      .update({
        next_type: selectedOption.option.nextType,
        next_node_id: selectedOption.option.nextNodeId,
      })
      .eq('id', selectedOption.option.id);
    setSaving(false);
    if (error) {
      toast({ title: 'שמירה נכשלה', description: error.message, variant: 'destructive' });
    } else {
      toast({ title: 'נשמר' });
    }
  }, [selectedOption, toast]);

  const publish = useCallback(async () => {
    if (!flow || !report?.ok) {
      toast({ title: 'לא ניתן לפרסם', description: 'יש שגיאות אימות לתקן', variant: 'destructive' });
      return;
    }
    setPublishing(true);
    try {
      // archive currently published, then mark this draft as published
      await supabase.from('personal_rx_flow_versions').update({ status: 'archived' }).eq('status', 'published');
      const { error } = await supabase
        .from('personal_rx_flow_versions')
        .update({
          status: 'published',
          published_at: new Date().toISOString(),
          snapshot: JSON.parse(JSON.stringify({ nodes: flow.nodes })),
        })
        .eq('id', flow.version.id);
      if (error) throw error;
      toast({ title: 'פורסם בהצלחה' });
      await reload();
    } catch (e: any) {
      toast({ title: 'פרסום נכשל', description: e?.message ?? String(e), variant: 'destructive' });
    } finally {
      setPublishing(false);
    }
  }, [flow, report, toast, reload]);

  if (loading) return <div className="p-6 text-sm text-muted-foreground">טוען…</div>;
  if (!flow) {
    return (
      <Card className="p-6 text-sm">
        אין עדיין טיוטת flow. הרץ את סקריפט המיגרציה כדי ליצור גרסה ראשונה.
      </Card>
    );
  }

  const questionOptions = flow.nodes
    .filter((n) => n.type === 'question' || n.type === 'category_selection')
    .map((n) => ({ id: n.id, label: `${n.label || n.key} (${n.key})` }));

  return (
    <div
      className={
        fullscreen
          ? 'fixed inset-0 z-50 bg-background p-4 grid grid-cols-[360px_1fr] gap-4'
          : 'grid grid-cols-[360px_1fr] gap-4'
      }
      style={fullscreen ? undefined : { height: '78vh' }}
    >
      <aside className="space-y-4 overflow-y-auto pe-1 order-first">
        <Card className="p-3 space-y-2">
          <div className="text-sm font-semibold">פרסום</div>
          {report && (
            <div className="text-[11px] space-y-1">
              <div>שגיאות: <span className={report.errors.length ? 'text-red-600 font-bold' : ''}>{report.errors.length}</span></div>
              <div>אזהרות: {report.warnings.length}</div>
              {report.errors.slice(0, 5).map((e, i) => (
                <div key={i} className="text-red-600">• {e.message}</div>
              ))}
            </div>
          )}
          <Button size="sm" disabled={!report?.ok || publishing} onClick={publish}>
            {publishing ? 'מפרסם…' : 'פרסם flow'}
          </Button>
        </Card>

        {!editingNode && !editingOption && (
          <Card className="p-3 text-[12px] text-muted-foreground">
            לחץ על שאלה או על תשובה בגרף כדי לערוך כאן בצד.
          </Card>
        )}

        {editingNode && (
          <NodeEditPanel
            key={editingNode.id}
            node={editingNode}
            onClose={() => setEditingNode(null)}
            onSaved={(patch) => {
              if (!flow) return;
              setFlow({
                ...flow,
                nodes: flow.nodes.map((n) => n.id === editingNode.id ? { ...n, ...patch } : n),
              });
              setEditingNode((prev) => prev ? { ...prev, ...patch } : prev);
            }}
          />
        )}

        {editingOption && (
          <OptionEditPanel
            key={editingOption.option.id}
            entry={editingOption}
            questionOptions={questionOptions}
            onClose={() => { setEditingOption(null); setSelectedOptionId(null); }}
            onSaved={(patch) => {
              if (!flow) return;
              setFlow({
                ...flow,
                nodes: flow.nodes.map((n) =>
                  n.id !== editingOption.parent.id ? n :
                  { ...n, options: n.options.map((o) => o.id === editingOption.option.id ? { ...o, ...patch } : o) }),
              });
              setEditingOption((prev) => prev ? { ...prev, option: { ...prev.option, ...patch } } : prev);
            }}
          />
        )}
      </aside>

      <div className="relative rounded-md border bg-slate-50 overflow-hidden">
        <div className="absolute top-2 start-2 z-10 flex items-center gap-2 bg-white/90 backdrop-blur rounded-md px-2 py-1 shadow-sm">
          <Badge variant="outline">גרסה {flow.version.version}</Badge>
          <Badge variant={flow.version.status === 'published' ? 'default' : 'secondary'}>
            {flow.version.status === 'draft' ? 'טיוטה' : flow.version.status === 'published' ? 'פורסם' : 'בארכיון'}
          </Badge>
          <span className="text-[11px] text-muted-foreground">
            {flow.nodes.length} צמתים · {flow.nodes.reduce((s, n) => s + n.options.length, 0)} מעברים
          </span>
        </div>
        <div className="absolute top-2 end-2 z-10 flex items-center gap-2 bg-white/90 backdrop-blur rounded-md px-2 py-1 shadow-sm">
          <span className="text-[11px] text-muted-foreground">קטגוריה:</span>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="h-7 text-[11px] w-[140px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">הכל</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setFullscreen((v) => !v)}>
            {fullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
          </Button>
        </div>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes as any}
          fitView
          onNodeClick={(_, n) => {
            if (n.id.startsWith('a_')) {
              const opt = n.data?.option as RxFlowOption | undefined;
              const parent = n.data?.parent as RxFlowNode | undefined;
              setSelectedOptionId(n.id.slice(2));
              setEditingNode(null);
              if (opt && parent) setEditingOption({ option: opt, parent });
            } else {
              const node = n.data?.node as RxFlowNode | undefined;
              setSelectedOptionId(null);
              setEditingOption(null);
              if (node) setEditingNode(node);
            }
          }}
          proOptions={{ hideAttribution: true }}
        >
          <Background gap={20} />
          <MiniMap pannable zoomable />
          <Controls position="bottom-right" />
        </ReactFlow>
      </div>
    </div>
  );
}

// ============= Inline edit dialogs =============

function NodeEditPanel({
  node,
  onClose,
  onSaved,
}: {
  node: RxFlowNode;
  onClose: () => void;
  onSaved: (patch: Partial<RxFlowNode>) => void;
}) {
  const { toast } = useToast();
  const [label, setLabel] = useState(node.label ?? '');
  const [hint, setHint] = useState(node.hint ?? '');
  const [placeholder, setPlaceholder] = useState(node.placeholder ?? '');
  const [questionType, setQuestionType] = useState<RxQuestionType>((node.questionType as RxQuestionType) ?? 'single');
  const [required, setRequired] = useState(node.required);
  const [saving, setSaving] = useState(false);
  const isQuestion = node.type === 'question';

  const save = async () => {
    setSaving(true);
    const patch: any = {
      label: label || null,
      hint: hint || null,
      placeholder: placeholder || null,
      required,
    };
    if (isQuestion) patch.question_type = questionType;
    const { error } = await supabase.from('personal_rx_flow_nodes').update(patch).eq('id', node.id);
    setSaving(false);
    if (error) {
      toast({ title: 'שמירה נכשלה', description: error.message, variant: 'destructive' });
      return;
    }
    onSaved({
      label: label || null,
      hint: hint || null,
      placeholder: placeholder || null,
      required,
      questionType: isQuestion ? questionType : node.questionType,
    });
    toast({ title: 'נשמר' });
  };

  return (
    <Card className="p-3 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold">עריכת שאלה</div>
        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={onClose}>סגור</Button>
      </div>
      <div className="space-y-3 text-sm">
        <div>
          <Label className="text-xs">מפתח (key)</Label>
          <Input value={node.key} disabled className="h-8 text-xs" />
        </div>
        <div>
          <Label className="text-xs">כותרת</Label>
          <Textarea value={label} onChange={(e) => setLabel(e.target.value)} rows={2} />
        </div>
        <div>
          <Label className="text-xs">טקסט עזר</Label>
          <Textarea value={hint} onChange={(e) => setHint(e.target.value)} rows={2} />
        </div>
        {isQuestion && (
          <>
            <div>
              <Label className="text-xs">Placeholder</Label>
              <Input value={placeholder} onChange={(e) => setPlaceholder(e.target.value)} className="h-8 text-xs" />
            </div>
            <div>
              <Label className="text-xs">סוג שאלה</Label>
              <Select value={questionType} onValueChange={(v) => setQuestionType(v as RxQuestionType)}>
                <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">בחירה יחידה</SelectItem>
                  <SelectItem value="multi">בחירה מרובה</SelectItem>
                  <SelectItem value="text">טקסט חופשי</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </>
        )}
        <div className="flex items-center gap-2">
          <Switch checked={required} onCheckedChange={setRequired} />
          <Label className="text-xs">חובה</Label>
        </div>
        <Button size="sm" onClick={save} disabled={saving} className="w-full">
          {saving ? 'שומר…' : 'שמור'}
        </Button>
      </div>
    </Card>
  );
}

function OptionEditPanel({
  entry,
  questionOptions,
  onClose,
  onSaved,
}: {
  entry: { option: RxFlowOption; parent: RxFlowNode };
  questionOptions: { id: string; label: string }[];
  onClose: () => void;
  onSaved: (patch: Partial<RxFlowOption>) => void;
}) {
  const { toast } = useToast();
  const [label, setLabel] = useState(entry.option.label);
  const [value, setValue] = useState(entry.option.value);
  const [nextType, setNextType] = useState<RxTransitionType>(entry.option.nextType);
  const [nextNodeId, setNextNodeId] = useState<string | null>(entry.option.nextNodeId);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    const { error } = await supabase
      .from('personal_rx_flow_options')
      .update({
        label,
        value,
        next_type: nextType,
        next_node_id: nextType === 'question' ? nextNodeId : null,
      })
      .eq('id', entry.option.id);
    setSaving(false);
    if (error) {
      toast({ title: 'שמירה נכשלה', description: error.message, variant: 'destructive' });
      return;
    }
    onSaved({ label, value, nextType, nextNodeId: nextType === 'question' ? nextNodeId : null });
    toast({ title: 'נשמר' });
  };

  return (
    <Card className="p-3 space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-sm font-semibold">עריכת תשובה</div>
        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={onClose}>סגור</Button>
      </div>
      <div className="text-xs text-muted-foreground">
        שאלה: {entry.parent.label || entry.parent.key}
      </div>
      <div className="space-y-3 text-sm">
        <div>
          <Label className="text-xs">תווית</Label>
          <Input value={label} onChange={(e) => setLabel(e.target.value)} className="h-8 text-xs" />
        </div>
        <div>
          <Label className="text-xs">ערך (value)</Label>
          <Input value={value} onChange={(e) => setValue(e.target.value)} className="h-8 text-xs" />
        </div>
        <div>
          <Label className="text-xs">סוג מעבר</Label>
          <Select value={nextType} onValueChange={(v) => setNextType(v as RxTransitionType)}>
            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(Object.keys(TRANSITION_LABEL) as RxTransitionType[]).map((t) => (
                <SelectItem key={t} value={t}>{TRANSITION_LABEL[t]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {nextType === 'question' && (
          <div>
            <Label className="text-xs">שאלת יעד</Label>
            <Select value={nextNodeId ?? ''} onValueChange={setNextNodeId}>
              <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="בחר שאלה" /></SelectTrigger>
              <SelectContent>
                {questionOptions.map((q) => (
                  <SelectItem key={q.id} value={q.id}>{q.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <Button size="sm" onClick={save} disabled={saving} className="w-full">
          {saving ? 'שומר…' : 'שמור'}
        </Button>
      </div>
    </Card>
  );
}
